import { useMemo } from 'react';
import { UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';

import {
  ExtractedTableJson,
  FieldSuggestion,
  type PartBotResults,
} from '@frontend/api';

import { useExtractedTables } from 'modules/extractedTables/ExtractedTableContext';
import { getActivePart } from 'modules/viewerPage/data/reducer';

import { useLivePartBotResults } from 'utils/hooks/taskTracking/useLivePartBotResults';
import { useLivePartDescriptorSuggestions } from 'utils/hooks/taskTracking/useLivePartDescriptorSuggestions';

export const usePartSetupSuggestionsInfo = (): {
  partBotResults: PartBotResults | null;
  suggestionsList: UseQueryResult<FieldSuggestion[], unknown>;
  extractedTablesData: ExtractedTableJson[] | undefined;
  hasSuggestions: boolean;
} => {
  const activePart = useSelector(getActivePart);
  const activePartUuid = activePart?.uuid;

  const partFileUuids = useMemo(
    () => (activePart?.partFiles?.map((pf) => pf.uuid) ?? []).sort(),
    [activePart?.partFiles],
  ) as string[];

  const { partBotResults } = useLivePartBotResults({
    partUuid: activePartUuid ?? '',
    partFileUuids,
  });

  const suggestionsList = useLivePartDescriptorSuggestions({
    partUuid: activePartUuid ?? '',
    partFileUuids,
  });

  const { extractedTables } = useExtractedTables();

  return {
    partBotResults: partBotResults ?? null,
    suggestionsList,
    extractedTablesData: extractedTables,
    hasSuggestions: Boolean(
      (partBotResults && partBotResults.raw.length > 0) ||
        (suggestionsList.data && suggestionsList.data.length > 0) ||
        (extractedTables && extractedTables.length > 0),
    ),
  };
};
